import { useEffect, useState } from "react";
import { onIdTokenChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { auth, functions as firebaseFunctions } from "../firebaseConfig";
import {getStoredOrgLoginContext} from "../utils/authRedirect";
import {getStaticCustomDomainMapping} from "../config/customDomainMappings";
import {resolveOrgContextFromLocation} from "../services/domainService";
import {isLightlyPlatformHost} from "../utils/routing/hostUtils";
import { invalidatePermissionsCache } from "../hooks/usePermissions";
import {clearJourneySessionHints, journeyWasChecked, markJourneyChecked, requestJourneyStatus} from "../services/phoneStartup";

// Reload mutates Firebase's user before refreshing its token. Retain this hint
// across retries/remounts so a failed token refresh cannot skip that boundary.
const verificationClaimsPending = new WeakSet();

// Payment returns are public receipt views, not profile-enrollment entry points.
// Keep their token/route intact; normal identity reconciliation resumes elsewhere.
export function isPaymentReturnPath(pathname) {
  return ["", "/:orgSlug", "/o/:orgSlug"].some(prefix =>
    ["/success", "/cancel", "/give/success", "/campaigns/:campaignId/success", "/c/:campaignId/success", "/fundraisers/:fundraiserId/success"]
      .some(suffix => Boolean(matchPath({path: prefix + suffix, end: true}, pathname))));
}

// Administrative preview already has an organization capability gate. Viewing a
// draft does not require attaching the staff account to a church person record.
export function isFundraiserAdminPreviewPath(pathname) {
  return Boolean(matchPath({path: '/platform/o/:orgSlug/campaigns/:campaignId/fundraisers/:slug/preview', end: true}, pathname));
}

// Delegated tasks authorize current membership and the explicit assignment on
// the server. They do not enroll staff in the private church People profile.
export function isDelegatedOpportunityTaskPath(pathname) {
  return Boolean(matchPath({path:'/platform/o/:orgSlug/opportunity-tasks',end:true},pathname));
}

// Public fundraiser viewing must not enroll an already signed-in visitor.
// ParticipantPage and its repository retain their existing visibility checks.
export function isPublicFundraiserViewPath(pathname) {
  return ["", "/:orgSlug", "/o/:orgSlug"].some(prefix =>
    ["/participant/:slug", "/campaigns/:campaignId/f/:slug", "/campaigns/:campaignId/fundraisers/:slug"]
      .some(suffix => Boolean(matchPath({path: prefix + suffix, end: true}, pathname))));
}

// Public event/campaign participation and an account's own records do not
// grant church membership. Their pages and callables keep separate guards.
export function isProfileOptionalParticipationPath(pathname) {
  return ["", "/:orgSlug", "/o/:orgSlug"].some(prefix =>
    ["/app/member-services", "/app/registrations", "/app/pledges", "/events/:eventId", "/campaigns/:campaignId", "/campaigns/:campaignId/donate"]
      .some(suffix => Boolean(matchPath({path:prefix+suffix,end:true},pathname))));
}

// Lightly Events never touches church membership or the People Directory:
// hosts and guests (including emails already in a directory) are not linked,
// onboarded or sent to /connect-profile on any event route.
export function isLightlyEventPath(pathname) {
  return ["/platform/events", "/platform/events/*", "/platform/e/:eventId", "/platform/e/:eventId/*"].some(path => Boolean(matchPath({path, end: true}, pathname)));
}

// Governance is platform-scoped. Its callable verifies the account and recent
// authentication; church profile linking must not run on this route.
export function isPlatformGovernancePath(pathname) {
  return Boolean(matchPath({path: "/platform/admin/governance", end: true}, pathname));
}

/**
 * One deterministic reconciliation attempt per authenticated session.
 *
 * The callable derives identity exclusively from Firebase's verified token and
 * performs tenant-scoped linking server-side. Failures never block sign-in;
 * ambiguous/conflicting identities remain an administrator review case.
 */
export default function IdentityLinkBootstrap() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(auth.currentUser || null);
  const [attempt, setAttempt] = useState(0);
  const [connectionError, setConnectionError] = useState(false);
  useEffect(() => {
    let previousUid = auth.currentUser?.uid;
    return onIdTokenChanged(auth, value => {
      if (!value || (previousUid && previousUid !== value.uid)) clearJourneySessionHints();
      previousUid = value?.uid;
      setUser(value);
    });
  }, []);
  useEffect(() => {
    const resume = () => setAttempt(n => n + 1);
    window.addEventListener('focus', resume);
    window.addEventListener('online', resume);
    return () => { window.removeEventListener('focus', resume); window.removeEventListener('online', resume); };
  }, []);
  useEffect(() => {
    setConnectionError(false);
    if (isPlatformGovernancePath(location.pathname)) return;
    if (isLightlyEventPath(location.pathname)) return;
    // These staff routes retain their authenticated organization capability gates.
    // Staff do not need a member/person profile to use it.
    if (isDelegatedOpportunityTaskPath(location.pathname)) return;
    if (['/platform/o/:orgSlug/teams', '/platform/o/:orgSlug/teams/*', '/platform/o/:orgSlug/team-workspaces/:teamId/*', '/admin/:orgSlug/teams', '/admin/:orgSlug/teams/*', '/platform/o/:orgSlug/children-teachers', '/platform/o/:orgSlug/children-classes', '/admin/:orgSlug/media-vault', '/platform/o/:orgSlug/media-vault'].some(path => matchPath({path,end:true},location.pathname)) || matchPath({path:'/admin/:orgSlug/visitors',end:true},location.pathname) || matchPath({path:'/platform/o/:orgSlug/notifications',end:true},location.pathname) || matchPath({path:'/platform/o/:orgSlug/announcements',end:true},location.pathname)) return;
    if (isPaymentReturnPath(location.pathname) || isFundraiserAdminPreviewPath(location.pathname) || isPublicFundraiserViewPath(location.pathname) || isProfileOptionalParticipationPath(location.pathname)) return;
    // A bearer preview must remain read-only even if its viewer is signed in.
    if (location.pathname.startsWith("/platform/website-preview/") || location.pathname === "/children-invitation" || location.pathname === "/member-invitation" || location.pathname === "/verify-phone") return;
    // Finish email/password actions before starting the member profile journey.
    // Otherwise verification can redirect away from its success/return screen.
    if (["/connect-profile", "/login", "/register", "/auth", "/verify-email", "/reset-password", "/action"].includes(location.pathname) || location.pathname.startsWith('/auth/') || !user || user.isAnonymous) return;
    let active = true;
    setConnectionError(false);
    (async () => {
      const orgId = await resolveIdentityOrganization(location.pathname, window.location.hostname);
      if (!active || auth.currentUser !== user) return;
      if (!orgId) { await reconcileSignedInUser(user); return; }
      // A verification link can be opened in another tab. Refresh both the local
      // user and its signed claims before asking the server to match identity.
      const needsVerificationRefresh = user.emailVerified !== true && !user.phoneNumber;
      if (needsVerificationRefresh) verificationClaimsPending.add(user);
      if (needsVerificationRefresh && user.reload) await user.reload();
      if (!active || auth.currentUser !== user) return;
      if (user.emailVerified === true && verificationClaimsPending.has(user)) {
        await user.getIdToken(true);
        verificationClaimsPending.delete(user);
      }
      if (!active || auth.currentUser !== user) return;
      if (journeyWasChecked(user, orgId)) return;
      const {data} = await requestJourneyStatus(user, orgId);
      if (!active) return;
      markJourneyChecked(user, orgId);
      if (["linked", "already_linked"].includes(data.state)) { invalidatePermissionsCache(); return; }
      if (["verify_phone", "no_match", "review_required"].includes(data.state)) navigate("/connect-profile?orgId=" + encodeURIComponent(orgId) + "&returnTo=" + encodeURIComponent(location.pathname + location.search), {replace:true});
    })().catch(() => { if (active && auth.currentUser === user) setConnectionError(true); });
    return () => {active = false;};
  }, [user, location.pathname, location.search, navigate, attempt]);

  return connectionError && user && auth.currentUser === user ? <div role="alert" className="p-3">We couldn’t connect your church profile. Your account permissions have not changed. <button type="button" onClick={() => setAttempt(n => n + 1)}>Retry profile connection</button></div> : null;
}

export async function resolveIdentityOrganization(pathname, hostname) {
  if (/^\/(platform\/o|admin|o)\//.test(pathname)) return inferOrgContextFromPathname(pathname);
  if (!isLightlyPlatformHost(hostname)) {
    const context = await resolveOrgContextFromLocation({hostname, pathname});
    if (!context?.orgId && !context?.orgSlug) throw new Error('Church domain could not be resolved');
    return context.orgId || context.orgSlug;
  }
  return getStaticCustomDomainMapping(hostname)?.orgId || inferOrgContextFromPathname(pathname) || getStoredOrgLoginContext().orgSlug || "";
}

export function inferOrgContextFromPathname(pathname = "") {
  const parts = String(pathname).split("/").filter(Boolean);
  if (parts[0] === "platform" && parts[1] === "o" && parts[2]) return parts[2];
  if (parts[0] === "admin" && parts[1]) return parts[1];
  if (parts[0] === "o" && parts[1]) return parts[1];
  const reserved = new Set(["", "me", "app", "o", "login", "register", "children-invitation", "member-invitation", "platform", "auth", "verify-email", "verify-phone", "connect-profile", "reset-password", "action", "privacy", "discover", "lightly"]);
  return parts[0] && !reserved.has(parts[0]) ? parts[0] : "";
}

export async function reconcileSignedInUser(user, orgId = "") {
    if (!user) return;
    const scope = String(orgId || "all").trim();
    const phoneOnly = Boolean(user.phoneNumber && !user.emailVerified);
    if (phoneOnly && !orgId) return;
    const key = `lightly:identity-reconciled:${user.uid}:${user.emailVerified === true}:${user.phoneNumber || ""}:${scope}`;
    try {
      if (sessionStorage.getItem(key) === "done") return;
      const ensureLink = httpsCallable(firebaseFunctions, phoneOnly ? "completePhoneRegistration" : "ensureIdentityLinkForCurrentUser");
      const result = await ensureLink(orgId ? {orgId} : {});
      if (result?.data?.ok !== false || result?.data?.reason !== "unavailable") {
        sessionStorage.setItem(key, "done");
      }
      if (Number(result?.data?.linkedCount || 0) > 0 || ["linked", "already_linked"].includes(result?.data?.state)) invalidatePermissionsCache();
    } catch {
      // Best effort by design. Access gates remain fail-closed and a later page
      // load or token refresh can retry without interrupting authentication.
    }
}
